import * as React from 'react';
import { Skeleton } from '@chess/api-skeleton';
import type { Color } from '@chess/api-model';

// One player beside the board, above it for the side at the top and below
// it for the side at the bottom: their name, flush with the board's left
// edge (nothing stands before or right after it: the line's place, above
// or below the board, already says which side it is); their rating when the game
// was played (muted); the "your move" badge after it while a puzzle waits
// for this side; and at the right end, pushed there together, the material
// that player has lost so far (whatever `material` the caller hands in: the
// `review.player.material` slot in the app) and the time they had left on
// the clock at the position shown (clocks.ts), muted, in the text colour
// while it is their move. When the board panel is narrow (under 26rem,
// board.css's container query) the rating and the material are hidden,
// so the name, the badge and the clock keep their room down to the
// panel's floor; they come back as the panel grows. The line's accessible
// label names the colour ("White: edblackknight"). Presentational; while
// the game's record loads, a skeleton
// holds the line's place so the board never moves.

export interface PlayerLineProps {
  color: Color;
  /** Undefined while the game's record loads. */
  player?: { name: string; rating?: number };
  /** A word after the name in the accent: "your move" while a puzzle waits for this side. */
  badge?: string;
  /** What stands just before the clock: the pieces this player has lost and their lead (the `review.player.material` slot). */
  material?: React.ReactNode;
  /** The time this player had left at the position shown, formatted ("4:32"); none when the game has no clocks. */
  clock?: string;
  /** Whether it is this player's move at the position shown: their clock reads in the text colour, the other's muted. */
  clockRunning?: boolean;
}

const SAMPLE = { name: 'edblackknight', rating: 1500 };

const Line: React.FC<Omit<PlayerLineProps, 'player'> & { player: { name: string; rating?: number } }> = ({ color, player, badge, material, clock, clockRunning }) => (
  <div className="board-player" role="group" aria-label={`${color === 'white' ? 'White' : 'Black'}: ${player.name}`}>
    <span className="board-player-name">{player.name}</span>
    {player.rating !== undefined && <span className="board-player-rating">{player.rating}</span>}
    {badge && <span className="board-player-badge">{badge}</span>}
    <span className="board-player-end">
      {material && <span className="board-player-material">{material}</span>}
      {clock && (
        <span className={`board-player-clock${clockRunning ? ' board-player-clock-running' : ''}`} aria-label={`${clock} left`}>
          {clock}
        </span>
      )}
    </span>
  </div>
);

export const PlayerLine: React.FC<PlayerLineProps> = ({ color, player, badge, material, clock, clockRunning }) => (
  <Skeleton loading={!player} shape={<Line color={color} player={SAMPLE} />} radius="0.25rem">
    {player && <Line color={color} player={player} badge={badge} material={material} clock={clock} clockRunning={clockRunning} />}
  </Skeleton>
);
